import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  FaSearch,
  FaFilter,
  FaDownload,
  FaEye,
  FaEdit,
  FaTrash,
  FaEnvelope,
  FaCreditCard,
  FaShieldAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaSort,
  FaChevronLeft,
  FaChevronRight,
  FaSync,
  FaExclamationTriangle,
  FaUser,
  FaShoppingCart,
  FaLock,
  FaUnlock,
  FaEyeSlash,
  FaKey,
  FaDatabase,
  FaHistory,
  FaChartLine,
  FaMoneyBillWave,
  FaReceipt,
  FaQrcode,
  FaMobile,
  FaDesktop
} from 'react-icons/fa';
import './PaymentManagement.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

const PaymentManagement = () => {
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPayments, setSelectedPayments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    gateway: '',
    paymentMethod: '',
    dateRange: '',
    startDate: '',
    endDate: '',
    amountRange: '',
    minAmount: '',
    maxAmount: ''
  });
  const [sortConfig, setSortConfig] = useState({
    key: 'createdAt',
    direction: 'desc'
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 10,
    totalItems: 0,
    totalPages: 0
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [showEncryptedData, setShowEncryptedData] = useState({});

  useEffect(() => {
    fetchPayments();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchPayments, 30000);
    return () => clearInterval(interval);
  }, [pagination.currentPage, sortConfig, filters]);

  useEffect(() => {
    applyFiltersAndSearch();
  }, [payments, searchTerm, filters]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: pagination.currentPage,
        limit: pagination.itemsPerPage,
        ...(filters.status && { status: filters.status }),
        ...(filters.gateway && { gateway: filters.gateway }),
        ...(filters.paymentMethod && { paymentMethod: filters.paymentMethod }),
        ...(filters.startDate && { startDate: filters.startDate }),
        ...(filters.endDate && { endDate: filters.endDate }),
        ...(filters.minAmount && { minAmount: filters.minAmount }),
        ...(filters.maxAmount && { maxAmount: filters.maxAmount })
      });

      const response = await axios.get(`${API_URL}/api/admin/payments?${params}`);
      const { payments: fetchedPayments, pagination: paginationData, total } = response.data;

      setPayments(fetchedPayments || []);
      setPagination(prev => ({
        ...prev,
        totalItems: total || 0,
        totalPages: paginationData?.pages || Math.ceil((total || 0) / prev.itemsPerPage)
      }));

    } catch (error) {
      console.error("Error fetching payments:", error);
      setError("Failed to fetch payments. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSearch = () => {
    let filtered = [...payments];

    // Apply search
    if (searchTerm) {
      filtered = filtered.filter(payment =>
        payment.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment.userEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment.paymentId?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply filters
    if (filters.status) {
      filtered = filtered.filter(payment => payment.paymentStatus === filters.status);
    }
    if (filters.gateway) {
      filtered = filtered.filter(payment => payment.paymentDetails?.gateway === filters.gateway);
    }
    if (filters.paymentMethod) {
      filtered = filtered.filter(payment => payment.paymentDetails?.paymentMethod === filters.paymentMethod);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      
      if (sortConfig.direction === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredPayments(filtered);
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handlePaymentStatusUpdate = async (paymentId, newStatus, note = '') => {
    try {
      await axios.patch(`${API_URL}/api/admin/payments/${paymentId}/status`, {
        status: newStatus,
        note
      });
      
      showSuccessMessage(`Payment status updated to ${newStatus}`);
      fetchPayments();
    } catch (error) {
      console.error("Error updating payment status:", error);
      showErrorMessage("Failed to update payment status");
    }
  };

  const handleRefund = async (paymentId, refundAmount, refundReason) => {
    try {
      const response = await axios.post(`${API_URL}/api/admin/payments/${paymentId}/refund`, {
        amount: refundAmount,
        reason: refundReason
      });
      
      showSuccessMessage(`Refund processed successfully. Refund ID: ${response.data.refundId}`);
      fetchPayments();
    } catch (error) {
      console.error("Error processing refund:", error);
      showErrorMessage("Failed to process refund");
    }
  };

  const handleBulkAction = async () => {
    if (!bulkAction || selectedPayments.length === 0) {
      showErrorMessage("Please select an action and payments");
      return;
    }

    try {
      await axios.post(`${API_URL}/api/admin/payments/bulk-action`, {
        action: bulkAction,
        paymentIds: selectedPayments
      });
      
      showSuccessMessage(`Bulk action '${bulkAction}' completed successfully`);
      setSelectedPayments([]);
      setBulkAction('');
      fetchPayments();
    } catch (error) {
      console.error("Error performing bulk action:", error);
      showErrorMessage("Failed to perform bulk action");
    }
  };

  const exportPayments = () => {
    const csvContent = [
      ['Order Number', 'Customer', 'Amount', 'Status', 'Gateway', 'Method', 'Date', 'Transaction ID'],
      ...filteredPayments.map(payment => [
        payment.orderNumber,
        payment.userName,
        `₹${payment.totalAmount?.toLocaleString()}`,
        payment.paymentStatus,
        payment.paymentDetails?.gateway || 'N/A',
        payment.paymentDetails?.paymentMethod || 'N/A',
        new Date(payment.createdAt).toLocaleDateString(),
        payment.paymentId || 'N/A'
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payments-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const showSuccessMessage = (message) => {
    // Implement toast notification
    alert(`Success: ${message}`);
  };

  const showErrorMessage = (message) => {
    // Implement toast notification
    alert(`Error: ${message}`);
  };

  const getPaymentStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <FaCheckCircle className="status-icon success" title="Paid" />;
      case 'pending':
        return <FaClock className="status-icon warning" title="Pending" />;
      case 'failed':
        return <FaTimesCircle className="status-icon error" title="Failed" />;
      case 'refunded':
        return <FaMoneyBillWave className="status-icon info" title="Refunded" />;
      default:
        return <FaExclamationTriangle className="status-icon warning" title="Unknown" />;
    }
  };

  const getGatewayIcon = (gateway) => {
    switch (gateway?.toLowerCase()) {
      case 'cashfree':
        return <FaCreditCard className="gateway-icon cashfree" title="Cashfree" />;
      case 'razorpay':
        return <FaCreditCard className="gateway-icon razorpay" title="Razorpay" />;
      case 'stripe':
        return <FaCreditCard className="gateway-icon stripe" title="Stripe" />;
      default:
        return <FaCreditCard className="gateway-icon default" title={gateway || 'Unknown'} />;
    }
  };

  const maskSensitiveData = (data, type = 'default') => {
    if (!data) return 'N/A';
    
    const str = data.toString();
    
    switch (type) {
      case 'transaction':
        return str.length > 4 ? `****${str.slice(-4)}` : '****';
      case 'card':
        return str.length > 4 ? `****${str.slice(-4)}` : '****';
      case 'upi':
        return str.includes('@') ? `****@${str.split('@')[1]}` : '****';
      default:
        return str.length > 4 ? `${str.slice(0, 2)}****${str.slice(-2)}` : '****';
    }
  };

  const toggleEncryptedData = (paymentId) => {
    setShowEncryptedData(prev => ({
      ...prev,
      [paymentId]: !prev[paymentId]
    }));
  };

  const PaymentDetailsModal = ({ payment, onClose }) => {
    if (!payment) return null;

    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content payment-details-modal" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Payment Details - {payment.orderNumber}</h2>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>
          
          <div className="modal-body">
            <div className="payment-details-grid">
              <div className="detail-section customer-info">
                <h3><FaUser /> Customer Information</h3>
                <div className="info-row">
                  <span className="label">Name:</span>
                  <span className="value">{payment.userName}</span>
                </div>
                <div className="info-row">
                  <span className="label">Email:</span>
                  <span className="value">{payment.userEmail}</span>
                </div>
                <div className="info-row">
                  <span className="label">Payment Date:</span>
                  <span className="value">{new Date(payment.createdAt).toLocaleString()}</span>
                </div>
              </div>

              <div className="detail-section payment-status">
                <h3><FaCreditCard /> Payment Status</h3>
                <div className="status-controls">
                  <select 
                    value={payment.paymentStatus} 
                    onChange={(e) => handlePaymentStatusUpdate(payment.id, e.target.value)}
                    className="status-select"
                  >
                    <option value="pending">Pending</option>
                    <option value="paid">Paid</option>
                    <option value="failed">Failed</option>
                    <option value="refunded">Refunded</option>
                  </select>
                  {getPaymentStatusIcon(payment.paymentStatus)}
                </div>
              </div>

              <div className="detail-section payment-info">
                <h3><FaMoneyBillWave /> Payment Information</h3>
                <div className="info-row">
                  <span className="label">Amount:</span>
                  <span className="value amount">₹{payment.totalAmount?.toLocaleString()}</span>
                </div>
                <div className="info-row">
                  <span className="label">Gateway:</span>
                  <span className="value">
                    {getGatewayIcon(payment.paymentDetails?.gateway)}
                    {payment.paymentDetails?.gateway || 'N/A'}
                  </span>
                </div>
                <div className="info-row">
                  <span className="label">Method:</span>
                  <span className="value">{payment.paymentDetails?.paymentMethod || 'N/A'}</span>
                </div>
              </div>

              <div className="detail-section transaction-info">
                <h3><FaReceipt /> Transaction Details</h3>
                <div className="info-row">
                  <span className="label">Transaction ID:</span>
                  <span className="value">
                    {maskSensitiveData(payment.paymentId, 'transaction')}
                    <button 
                      className="toggle-encrypted-btn"
                      onClick={() => toggleEncryptedData(payment.id)}
                      title={showEncryptedData[payment.id] ? "Hide sensitive data" : "Show sensitive data"}
                    >
                      {showEncryptedData[payment.id] ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </span>
                </div>
                {showEncryptedData[payment.id] && (
                  <div className="encrypted-data">
                    <div className="info-row">
                      <span className="label">Full Transaction ID:</span>
                      <span className="value encrypted">{payment.paymentId || 'N/A'}</span>
                    </div>
                    <div className="info-row">
                      <span className="label">Gateway Order ID:</span>
                      <span className="value encrypted">{payment.paymentDetails?.gatewayOrderId || 'N/A'}</span>
                    </div>
                    <div className="info-row">
                      <span className="label">Session ID:</span>
                      <span className="value encrypted">{payment.paymentDetails?.paymentSessionId || 'N/A'}</span>
                    </div>
                  </div>
                )}
              </div>

              {payment.paymentDetails?.paymentTime && (
                <div className="detail-section payment-timing">
                  <h3><FaClock /> Payment Timing</h3>
                  <div className="info-row">
                    <span className="label">Payment Time:</span>
                    <span className="value">{new Date(payment.paymentDetails.paymentTime).toLocaleString()}</span>
                  </div>
                </div>
              )}

              {payment.statusHistory && payment.statusHistory.length > 0 && (
                <div className="detail-section payment-history">
                  <h3><FaHistory /> Payment History</h3>
                  <div className="history-list">
                    {payment.statusHistory.map((status, index) => (
                      <div key={index} className="history-item">
                        <div className="history-status">{status.status}</div>
                        <div className="history-date">
                          {new Date(status.timestamp).toLocaleString()}
                        </div>
                        {status.note && <div className="history-note">{status.note}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button 
              className="btn btn-secondary"
              onClick={() => {
                const refundAmount = prompt("Enter refund amount (leave empty for full refund):");
                const refundReason = prompt("Enter refund reason:");
                if (refundAmount !== null) {
                  handleRefund(payment.id, refundAmount || payment.totalAmount, refundReason);
                }
              }}
            >
              <FaMoneyBillWave /> Process Refund
            </button>
            <button 
              className="btn btn-primary"
              onClick={() => {
                // Send payment notification email
                axios.post(`${API_URL}/api/admin/payments/${payment.id}/send-notification`)
                  .then(() => showSuccessMessage("Payment notification sent"))
                  .catch(() => showErrorMessage("Failed to send notification"));
              }}
            >
              <FaEnvelope /> Send Notification
            </button>
            <button className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="payment-management">
      <div className="page-header">
        <h1><FaCreditCard /> Payment Management</h1>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={fetchPayments}>
            <FaSync /> Refresh
          </button>
          <button className="btn btn-primary" onClick={exportPayments}>
            <FaDownload /> Export
          </button>
        </div>
      </div>

      <div className="controls-section">
        <div className="search-filter-row">
          <div className="search-box">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search payments..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <button 
            className="btn btn-secondary filter-toggle"
            onClick={() => setShowFilters(!showFilters)}
          >
            <FaFilter /> Filters
          </button>
        </div>

        {showFilters && (
          <div className="filters-panel">
            <div className="filter-row">
              <div className="filter-group">
                <label>Status:</label>
                <select 
                  value={filters.status} 
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                  <option value="failed">Failed</option>
                  <option value="refunded">Refunded</option>
                </select>
              </div>

              <div className="filter-group">
                <label>Gateway:</label>
                <select 
                  value={filters.gateway} 
                  onChange={(e) => setFilters(prev => ({ ...prev, gateway: e.target.value }))}
                >
                  <option value="">All Gateways</option>
                  <option value="cashfree">Cashfree</option>
                  <option value="razorpay">Razorpay</option>
                  <option value="stripe">Stripe</option>
                </select>
              </div>

              <div className="filter-group">
                <label>Payment Method:</label>
                <select 
                  value={filters.paymentMethod} 
                  onChange={(e) => setFilters(prev => ({ ...prev, paymentMethod: e.target.value }))}
                >
                  <option value="">All Methods</option>
                  <option value="credit_card">Credit Card</option>
                  <option value="debit_card">Debit Card</option>
                  <option value="upi">UPI</option>
                  <option value="net_banking">Net Banking</option>
                  <option value="wallet">Wallet</option>
                </select>
              </div>
            </div>

            <div className="filter-row">
              <div className="filter-group">
                <label>Start Date:</label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                />
              </div>

              <div className="filter-group">
                <label>End Date:</label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                />
              </div>

              <div className="filter-group">
                <label>Min Amount:</label>
                <input
                  type="number"
                  placeholder="Min amount"
                  value={filters.minAmount}
                  onChange={(e) => setFilters(prev => ({ ...prev, minAmount: e.target.value }))}
                />
              </div>

              <div className="filter-group">
                <label>Max Amount:</label>
                <input
                  type="number"
                  placeholder="Max amount"
                  value={filters.maxAmount}
                  onChange={(e) => setFilters(prev => ({ ...prev, maxAmount: e.target.value }))}
                />
              </div>
            </div>
          </div>
        )}

        <div className="bulk-actions">
          <select 
            value={bulkAction} 
            onChange={(e) => setBulkAction(e.target.value)}
            className="bulk-action-select"
          >
            <option value="">Bulk Actions</option>
            <option value="export">Export Selected</option>
            <option value="mark_paid">Mark as Paid</option>
            <option value="mark_failed">Mark as Failed</option>
          </select>
          
          <button 
            className="btn btn-primary"
            onClick={handleBulkAction}
            disabled={!bulkAction || selectedPayments.length === 0}
          >
            Apply
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading payments...</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : (
        <>
          <div className="payments-table">
            <table>
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={selectedPayments.length === filteredPayments.length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedPayments(filteredPayments.map(p => p.id));
                        } else {
                          setSelectedPayments([]);
                        }
                      }}
                    />
                  </th>
                  <th onClick={() => handleSort('orderNumber')}>
                    Order Number <FaSort />
                  </th>
                  <th onClick={() => handleSort('userName')}>
                    Customer <FaSort />
                  </th>
                  <th onClick={() => handleSort('totalAmount')}>
                    Amount <FaSort />
                  </th>
                  <th onClick={() => handleSort('paymentStatus')}>
                    Status <FaSort />
                  </th>
                  <th>Gateway</th>
                  <th>Method</th>
                  <th onClick={() => handleSort('createdAt')}>
                    Date <FaSort />
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPayments.map(payment => (
                  <tr key={payment.id} className={payment.paymentStatus}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedPayments.includes(payment.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedPayments(prev => [...prev, payment.id]);
                          } else {
                            setSelectedPayments(prev => prev.filter(id => id !== payment.id));
                          }
                        }}
                      />
                    </td>
                    <td>{payment.orderNumber}</td>
                    <td>
                      <div className="customer-info">
                        <div className="customer-name">{payment.userName}</div>
                        <div className="customer-email">{payment.userEmail}</div>
                      </div>
                    </td>
                    <td className="amount">₹{payment.totalAmount?.toLocaleString()}</td>
                    <td>
                      <div className="status-cell">
                        {getPaymentStatusIcon(payment.paymentStatus)}
                        <span className="status-text">{payment.paymentStatus}</span>
                      </div>
                    </td>
                    <td>
                      <div className="gateway-cell">
                        {getGatewayIcon(payment.paymentDetails?.gateway)}
                        <span>{payment.paymentDetails?.gateway || 'N/A'}</span>
                      </div>
                    </td>
                    <td>{payment.paymentDetails?.paymentMethod || 'N/A'}</td>
                    <td>{new Date(payment.createdAt).toLocaleDateString()}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-icon"
                          onClick={() => {
                            setSelectedPayment(payment);
                            setShowPaymentDetails(true);
                          }}
                          title="View Details"
                        >
                          <FaEye />
                        </button>
                        <button
                          className="btn btn-icon"
                          onClick={() => handlePaymentStatusUpdate(payment.id, 'paid')}
                          title="Mark as Paid"
                        >
                          <FaCheckCircle />
                        </button>
                        <button
                          className="btn btn-icon"
                          onClick={() => handlePaymentStatusUpdate(payment.id, 'failed')}
                          title="Mark as Failed"
                        >
                          <FaTimesCircle />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pagination">
            <button
              onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
              disabled={pagination.currentPage === 1}
            >
              <FaChevronLeft /> Previous
            </button>
            
            <span className="page-info">
              Page {pagination.currentPage} of {pagination.totalPages}
            </span>
            
            <button
              onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
              disabled={pagination.currentPage === pagination.totalPages}
            >
              Next <FaChevronRight />
            </button>
          </div>
        </>
      )}

      {showPaymentDetails && (
        <PaymentDetailsModal
          payment={selectedPayment}
          onClose={() => {
            setShowPaymentDetails(false);
            setSelectedPayment(null);
          }}
        />
      )}
    </div>
  );
};

export default PaymentManagement; 